import { AxiosError, AxiosInstance, HttpStatusCode } from "axios";
import { BaseError } from "core/utils/baseError";

export type QuickSightDashboardRequest = {
  account_ids: string[];
};

/**
 * @typedef {Object} InvitationRequest
 * @property {string} context_id
 * @property {string} email
 * @property {string} first_name
 */
export type InvitationRequest = {
  context_id: string;
  email: string;
  first_name: string;
};

export type QuickSightDashboardResponse = {
  embed_url: string;
};

export type GetUserRoleAssignmentsResponse = {
  account: {
    id: number;
    org_id: number;
    name: string;
    number: string;
    brand_id: number;
    display_name: string;
  };
};

export type UserData = {
  id: number;
  username: string;
  cognito_username: string;
  organization_id: number;
  first_name: string;
  last_name: string;
  is_organization_admin: boolean;
  primary_contact_id?: number;
};

export type AccountData = {
  id: number;
  org_id: number;
  name: string;
  number: string;
  brand_id: number;
  display_name: string;
  is_adhoc: boolean;
};

export type OrganizationData = {
  id: number;
  display_name: string;
  name: string;
};

export type UserRoleAssignmentData = {
  id: number;
  account: string;
  role: string;
};

export type UserRoleData = {
  account_number: string;
  role: string;
};

export type GetOrganizationUserResponse = {
  user: UserData;
  organization: OrganizationData;
  accounts: AccountData[];
  roles: UserRoleData[];
};

export type ListUserShape = {
  user: UserData;
  accounts: AccountData[];
  roles: UserRoleData[];
  organization: OrganizationData;
};

export type ListUsersResponse = {
  users: ListUserShape[];
  pending_invites: ListUserShape[];
};

export type LoginRequest = {
  email: string;
  password: string;
};

export type ChangePasswordRequest = {
  userId: number;
  currPass: string;
  newPass: string;
};

export type UpdatePasswordRequest = {
  email: string;
  tempPassword: string;
  newPassword: string;
};

export type ForgotPasswordRequest = {
  email: string;
  password: string;
  code?: string;
};

export type LoginResponse = { token: string; refresh: string; user_id: number };

export type OrganizationRefreshTokenRequest = {
  token: string;
  cognito_username: string;
};

export type OrganizationRefreshTokenResponse = { token: string };

export type RevokeTokenRequest = { user_id: number; refresh_token: string };

export type UpdateUserRequest = {
  is_organization_admin?: boolean;
  accounts: Omit<
    UserRoleAssignmentData,
    "account" | "account_number" | "is_adhoc"
  >[];
  username?: string;
  primary_contact_id?: string;
  last_name?: string;
};

export type CreateUserRequest = {
  email: string;
  first_name: string;
  last_name: string;
  is_organization_admin: boolean;
  accounts: Omit<
    UserRoleAssignmentData,
    "account" | "account_number" | "is_adhoc"
  >[];
};

export type CreateUserResponse = UserData;

export type getQuickSightDashboardRequest = {
  account_number: string;
  context_id?: string;
};

export type GetReportsListResponse = {
  id: string | null;
  month: number;
  year: number;
  created_at: string;
  status: string;
};

export type GetReportResponse = {
  url: string | null;
};

export type PostGenerateReportResponse = {
  id: string;
};

class ManagementBackendClient {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  /**
   * Gets the quicksight dashboard for the given account number
   */
  async getQuickSightDashboard(
    req: getQuickSightDashboardRequest,
  ): Promise<QuickSightDashboardResponse> {
    return await this.client
      .post("/web/quicksight", req)
      .then((res) => {
        if (res.status !== 200) {
          throw new BaseError(
            `Request getQuicksightDashboard failed with status ${res.status}`,
            {
              context: { place: res.config.url, status: res.status },
              cause: res.data,
            },
          );
        }
        return { embed_url: res.data.embed_url };
      })
      .catch((err) => {
        if (err instanceof AxiosError) {
          throw BaseError.fromAxiosError(err);
        }

        if (err instanceof Error) {
          throw BaseError.fromError(err);
        }

        throw new BaseError("An unknown error occurred", {
          context: { place: "getQuickSightDashboard" },
          cause: String(err),
        });
      });
  }

  async getQuickSightOrganizationDashboard(): Promise<QuickSightDashboardResponse> {
    try {
      return await this.client
        .post("/web/organization-quicksight")
        .then((res) => {
          if (res.status !== 200) {
            throw Error("request status: " + res.status);
          }
          return res.data;
        });
    } catch (error) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("unknown error");
    }
  }

  async getUserRoleAssignments(
    userId: number,
  ): Promise<GetUserRoleAssignmentsResponse[]> {
    return await this.client
      .get("/web/user-role-assignments", {
        params: { user_id: userId },
      })
      .then((res) => res.data);
  }

  /**
   * Retrieves the organization user for the given user id
   *
   * check out {@link https://special-doodle-n1g8lke.pages.github.io/#/operations/web_get_user_details}
   * for documentation
   *
   * @param {number} userId
   * @throws {BaseError}
   * @returns {Promise<GetOrganizationUserResponse>}
   */
  async getOrganizationUser(
    userId: number,
  ): Promise<GetOrganizationUserResponse> {
    try {
      return await this.client
        .get(`/web/users/${userId}`)
        .then((res) => res.data);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error", {
        context: { place: "getOrganizationUser" },
        cause: String(error),
      });
    }
  }

  async login(request: LoginRequest): Promise<LoginResponse> {
    return this.client.post("/web/login", request).then((res) => res.data);
  }

  async organizationRefreshToken(request: OrganizationRefreshTokenRequest) {
    try {
      return await this.client
        .post("/web/organization-refresh-token", request)
        .then((res) => res.data);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("unknown error");
    }
  }

  async changePassword({ userId, currPass, newPass }: ChangePasswordRequest) {
    try {
      this.client.defaults.headers["Access-Control-Allow-Origin"] = "*";
      const response = await this.client.patch(
        `/web/change_password/${userId}`,
        { current_password: currPass, new_password: newPass },
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("Unknown Error");
    }
  }

  async forgotPassword(email: string) {
    return this.client
      .post("/web/forgot-password", { email })
      .then((res) => res.data);
  }

  async replacePassword(request: UpdatePasswordRequest) {
    try {
      const response = await this.client.post(
        "/web/replace_temporary_password",
        {
          email: request?.email,
          temporary_password: request?.tempPassword,
          new_password: request?.newPassword,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("Unknown Error");
    }
  }

  async forgetPasswordVerification({
    email,
    password,
    code,
  }: ForgotPasswordRequest) {
    try {
      const res = await this.client.post("/web/forgot-password-verification", {
        email,
        password,
        code,
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("unknown error");
    }
  }

  /**
   * Revokes the user's refresh token, invalidating their session.
   *
   * This method sends a POST request to revoke a user's token.
   * Only authenticated users can call this.
   *
   * Check out {@link https://special-doodle-n1g8lke.pages.github.io/#/operations/revoke_token}
   * for documentation.
   *
   * @param {RevokeTokenRequest} request - The request object containing the user's `user_id` and `refresh_token`.
   * @throws {BaseError} Throws a custom error if the request fails.
   * @returns {Promise<HttpStatusCode>} The HTTP status of the response.
   */
  async revokeToken(request: RevokeTokenRequest): Promise<HttpStatusCode> {
    try {
      return await this.client
        .post("/web/revoke-token", request)
        .then((res) => res.status);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("unknown error");
    }
  }

  /**
   * Retrieves a list of users belonging to a specific organization and returns
   * them as JSON. Includes a list of all users in the organization as well as
   * a list of pending invites. Only organization admins can call this.
   *
   * check out {@link https://special-doodle-n1g8lke.pages.github.io/#/operations/web_list_users}
   * for documentation
   *
   * @throws {BaseError}
   * @returns {Promise<ListUsersResponse>}
   */
  async listUsers(): Promise<ListUsersResponse> {
    try {
      return await this.client
        .get<ListUsersResponse>("/web/users")
        .then((res) => res.data);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error", {
        context: { place: "listUsers" },
        cause: String(error),
      });
    }
  }

  /**
   * Retrieves a list of accounts belonging to a specific organization
   * Only organization admins can call this.
   *
   * check out {@link https://special-doodle-n1g8lke.pages.github.io/#/operations/web_list_accounts}
   * for documentation
   *
   * @param {number} organizationId
   * @throws {BaseError}
   * @returns {Promise<AccountData[]>}
   */
  async listAccounts(organizationId: number): Promise<AccountData[]> {
    try {
      return await this.client
        .get<AccountData[]>(`web/organizations/${organizationId}/accounts`)
        .then((res) => res.data);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error", {
        context: { place: "listUsers" },
        cause: String(error),
      });
    }
  }

  async getOrganizationDetails(
    organizationId: number,
  ): Promise<OrganizationData> {
    try {
      return await this.client
        .get<OrganizationData>(`web/organizations/${organizationId}`)
        .then((res) => res.data);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("Unknown Error");
    }
  }

  /*
  This function returns the request status - returns 204 upon successful deletion
   */
  async deleteUser(userId: number): Promise<number> {
    try {
      const response = await this.client.delete(`web/users/${userId}`);
      return response.status;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("Unknown Error");
    }
  }

  /*
  This function returns the request status - returns 204 upon successful deletion
   */
  async updateUser(userId: number, body: UpdateUserRequest): Promise<number> {
    try {
      const response = await this.client.patch(`web/users/${userId}`, body);
      return response.status;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("Unknown error");
    }
  }

  async resendTemporaryPassword(email: string): Promise<number> {
    try {
      const response = await this.client.post(`web/resend-temporary-password`, {
        email,
      });
      return response.status;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw error;
      }
      throw new Error("Unknown error");
    }
  }

  /**
   * Checks if the given context account and account number match
   *
   * @param {Object} body
   * @param {string} body.context_id
   * @param {string} body.account_number
   * @returns {Promise<boolean>}
   * @throws {BaseError}
   */
  async checkContextAccount(body: {
    context_id: string;
    account_number: string;
  }): Promise<boolean> {
    try {
      const response = await this.client.post<boolean>(`check-ctx`, body);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error", {
        context: { place: "checkContextAccount" },
        cause: String(error),
      });
    }
  }

  /**
   * Fetches the report list based on the provided account and transaction type.
   *
   * @param {string} transactionType - The type of report to fetch (e.g., 'all').
   * @param {string} accountId - The account number for which reports are being requested.
   * @returns {Promise<GetReportsListResponse[]>}
   * @throws {BaseError}
   */
  async getReportsList(
    transactionType: string,
    accountId: string,
  ): Promise<GetReportsListResponse[]> {
    try {
      const response = await this.client.get<GetReportsListResponse[]>(
        `web/transactions/list-reports?account_number=${accountId}&report_type=${transactionType}`,
      );
      if (response.status !== 200) {
        throw new BaseError(
          `Request getReportsList failed with status ${response.status}`,
          {
            context: { place: response.config.url, status: response.status },
            cause: response.data,
          },
        );
      }
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error ", {
        context: { place: "getReportsList" },
        cause: String(error),
      });
    }
  }

  /**
   * Fetches the report list based on the provided account and transaction type.
   *
   * @param {string} transactionType - The type of report to fetch (e.g., 'all').
   * @param {string} accountId - The account number for which reports are being requested.
   * @returns {Promise<GetReportsListResponse[]>}
   * @throws {BaseError}
   */
  async getReconciliationReportsList(
    transactionType: string,
    accountId: string,
  ): Promise<GetReportsListResponse[]> {
    try {
      const response = await this.client.get<GetReportsListResponse[]>(
        `web/reconciliations/list-reports?account_number=${accountId}&report_type=${transactionType}`,
      );
      if (response.status !== 200) {
        throw new BaseError(
          `Request getReconcoliationsReportsList failed with status ${response.status}`,
          {
            context: { place: response.config.url, status: response.status },
            cause: response.data,
          },
        );
      }
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error ", {
        context: { place: "getReconciliationReportsList" },
        cause: String(error),
      });
    }
  }

  /**
   * Fetches a signed URL for a specific transaction report based on the provided unique identifier for a generated report.
   *
   * @param {string} id - The specific report to fetch.
   * @returns {Promise<GetReportResponse>}
   * @throws {BaseError}
   */

  async getReconciliationReport(id: string | null): Promise<GetReportResponse> {
    try {
      const response = await this.client.get<GetReportResponse>(
        `web/reconciliations/get-report?id=${id}`,
      );
      if (response.status !== 200) {
        throw new BaseError(
          `Request getReconciliationsReport failed with status ${response.status}`,
          {
            context: { place: response.config.url, status: response.status },
            cause: response.data,
          },
        );
      }
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error ", {
        context: { place: "getReconciliationsReport" },
        cause: String(error),
      });
    }
  }

  /**
   * Fetches a signed URL for a specific transaction report based on the provided unique identifier for a generated report.
   *
   * @param {string} id - The specific report to fetch.
   * @returns {Promise<GetReportResponse>}
   * @throws {BaseError}
   */

  async getReport(id: string | null): Promise<GetReportResponse> {
    try {
      const response = await this.client.get<GetReportResponse>(
        `web/transactions/get-report?id=${id}`,
      );
      if (response.status !== 200) {
        throw new BaseError(
          `Request getReport failed with status ${response.status}`,
          {
            context: { place: response.config.url, status: response.status },
            cause: response.data,
          },
        );
      }
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error ", {
        context: { place: "getReport" },
        cause: String(error),
      });
    }
  }

  /**
   * Handler for initiating the transaction report generation based on the provided account number, year, month and report type.
   *
   * @param {string} account - The account number for which the report is generated.
   * @param {number} year - The year of the report.
   * @param {string} reportType - The type of transaction report (e.g., "all").
   * @param {number} month - The month for which the report is generated.
   * @returns {Promise<PostGenerateReportResponse>}
   * @throws {BaseError}
   */

  async postGenerateReport(
    account: string,
    year: number,
    reportType: string,
    month: number,
  ): Promise<PostGenerateReportResponse> {
    try {
      const requestBody = {
        account,
        year,
        report_type: reportType,
        month,
      };
      const response = await this.client.post<PostGenerateReportResponse>(
        `web/transactions/generate-report`,
        requestBody,
      );
      if (response.status !== 200) {
        throw new BaseError(
          `Request getReport failed with status ${response.status}`,
          {
            context: { place: response.config.url, status: response.status },
            cause: response.data,
          },
        );
      }
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error ", {
        context: { place: "postGenerateReport" },
        cause: String(error),
      });
    }
  }

  /**
   * Handler for initiating the transaction report generation based on the provided account number, year, month and report type.
   *
   * @param {string} account - The account number for which the report is generated.
   * @param {number} year - The year of the report.
   * @param {string} reportType - The type of transaction report (e.g., "all").
   * @param {number} month - The month for which the report is generated.
   * @returns {Promise<PostGenerateReportResponse>}
   * @throws {BaseError}
   */

  async postGenerateReconciliationReport(
    account: string,
    year: number,
    reportType: string,
    month: number,
  ): Promise<PostGenerateReportResponse> {
    try {
      const requestBody = {
        account,
        year,
        report_type: reportType,
        month,
      };
      const response = await this.client.post<PostGenerateReportResponse>(
        `web/reconciliations/generate-report`,
        requestBody,
      );
      if (response.status !== 200) {
        throw new BaseError(
          `Request getReconciliationReport failed with status ${response.status}`,
          {
            context: { place: response.config.url, status: response.status },
            cause: response.data,
          },
        );
      }
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error ", {
        context: { place: "postGenerateReconciliationReport" },
        cause: String(error),
      });
    }
  }

  async requestInvitation(req: InvitationRequest) {
    try {
      return await this.client
        .post("web/users/request-invitation", req)
        .then((res) => res.status);
    } catch (error) {
      if (error instanceof AxiosError) {
        throw BaseError.fromAxiosError(error);
      }

      if (error instanceof Error) {
        throw BaseError.fromError(error);
      }

      throw new BaseError("Unknown error", {
        context: { place: "requestInvitationRequest" },
        cause: String(error),
      });
    }
  }
}

export default ManagementBackendClient;
